import 'babel-polyfill';
import 'array.prototype.find';
import 'es6-object-assign';
import 'string.prototype.startswith';
import 'array-includes';

import React from 'react';
import { render } from 'react-dom';
import { Provider } from 'react-redux';

import { createBrowserHistory } from 'history';
import { ConnectedRouter, routerMiddleware } from 'connected-react-router';

import { applyMiddleware, compose, createStore } from 'redux';
import createSagaMiddleware from 'redux-saga';

import * as HttpStatus from 'http-status-codes';

import createRootReducer from './reducers';
import appStart from './sagas';
import Application from './components/Application/ApplicationContainer';
import './assets/style/vendors/index.css';
import './assets/style/index.scss';

import DevTools from './components/DevTools';
import * as actions from './actions/chatActions';
import * as endpoints from './constants/ApiEndpoints';
import { hasAuth, removeAuth } from './helpers/AuthUtils';
import { setMemberId } from './helpers/UserUtils';
import { INBOX_MESSENGER_LOGIN, LOGGING_OUT } from './constants/AppConstants';
import { _postMessage, bindEvent } from './helpers/BrowserUtils';

const isProduction = process.env.NODE_ENV === 'production';

const history = createBrowserHistory();

const sagaMiddleware = createSagaMiddleware();

export let store;
if (isProduction) {
  store = createStore(
    createRootReducer(history),
    compose(
      applyMiddleware(
        routerMiddleware(history),
        sagaMiddleware,
      ),
    ),
  );
} else {
  // Only use the DevTools component when in development.
  const enhancer = DevTools.instrument();
  store = createStore(
    createRootReducer(history),
    compose(
      applyMiddleware(
        routerMiddleware(history),
        sagaMiddleware,
      ),
      enhancer,
    ),
  );
}

/**
 * If this value is set, the OAuth flow redirected us back here during logout.
 * We know the user won't be authenticated, no need to go any further.
 */
if (hasAuth(LOGGING_OUT)) {
  removeAuth();
  window.location.href = endpoints.MEMBER_LOGIN;
} else {
  const parentWindow = window.parent;
  const isIFrame = window !== window.parent;
  // if we are an iFrame, then we are Inbox-Messenger and we have to start the app more cautiously
  if (isIFrame) {
    console.log('Inbox Messenger is attempting to start up');
    _postMessage(parentWindow, { eventName: 'messenger-started' }, '*');

    bindEvent(window, 'message', function (event) {
      /*
       * event.data object -> { eventType: 'inbox-chat-login', memberId: 1 }
       * Blocking the start of this app until this message is received, for security reasons
       */
      if (event.data.eventType === INBOX_MESSENGER_LOGIN) {
        const memberId = event.data.memberId;
        console.log(`Messenger: Member [${memberId}] is attempting to login via Inbox.`);
        setMemberId(memberId);
        sagaStart();
      }
    });
  } else {
    sagaStart();
  }
}

// Wait for socket connection before rendering anything
/** Using Router instead of BrowserRouter to share history with the Redux ConnectedRouter */

function sagaStart() {
  const saga = sagaMiddleware.run(appStart);

  saga
    .toPromise()
    .then(
      (appStartError) => {
        if (appStartError === HttpStatus.UNAUTHORIZED) {
          return;
        } else if (!appStartError) {
          store.dispatch(actions.getAppStartData());
        }

        render(!appStartError ? <AppStartSuccess/> : <AppStartError/>, document.getElementById('app'));
      },
      (appStartError) => {
        // If the user is unauthenticated, a redirect is coming, no need to render anything
        if (appStartError === HttpStatus.UNAUTHORIZED) {
          return;
        }

        render(<AppStartError/>, document.getElementById('app'));
      }
    );

  const AppStartSuccess = () => (
    <Provider store={store}>
      <ConnectedRouter history={history}>
        <>
          <Application history={history}/>
          {!isProduction && <DevTools/>}
        </>
      </ConnectedRouter>
    </Provider>
  );

  const AppStartError = () => (
    <div id="loadingContainer">
      <div id="dummySideBar"/>
      <div id="dummyContentArea">
        <h1>Oops! Something went wrong.</h1>
        <button className="btn primary" onClick={refreshPage}>
          Refresh Page
        </button>
      </div>
    </div>
  );

  const refreshPage = () => {
    removeAuth();
    window.location.reload();
  };
}
